import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';
import type { WatchQueryFetchPolicy } from 'apollo-client';
import type { GraphQLError } from 'graphql';

import { useIsLicensed } from '@confluence/session-data/entry-points/useAccessStatus.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { isStatusCodeError } from '@confluence/error-boundary/entry-points/isStatusCodeError.ts';
import { expValEquals } from '@confluence/feature-experiments';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import type { FetchPolicy } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';

// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { SitePersonalizationQuery } from './SitePersonalizationQuery.experimentalgraphql.ts';
import type {
	SitePersonalizationQuery as SitePersonalizationQueryData,
	SitePersonalizationQuery_experimentalSitePersonalization_attributes as SitePersonalizationAttributes,
	SitePersonalizationQueryVariables,
} from './__types__/SitePersonalizationQuery.ts';
import { useExperimentalSitePersonalizationQuery } from './useExperimentalSitePersonalizationQuery.ts';

export type useSitePersonalizationQueryProps = {
	cloudId: string;
	skip?: boolean;
	fetchPolicy?: WatchQueryFetchPolicy;
};

export let skipQueryIfUnauthorized = false;

export const useSitePersonalizationQuery = ({
	cloudId,
	skip,
	fetchPolicy,
}: {
	cloudId: string;
	skip?: boolean;
	fetchPolicy?: WatchQueryFetchPolicy;
}) => {
	const isLicensed = useIsLicensed();

	const skipOptions =
		skip ||
		!isLicensed ||
		skipQueryIfUnauthorized ||
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		Boolean(process.env.REACT_SSR); // SitePersonalizationQuery isn't preloaded and can be skipped during SSR to avoid unnecessary requests

	const queryArgs = {
		variables: {
			cloudId,
		},
		fetchPolicy: (fetchPolicy || 'cache-first') as FetchPolicy,
		skip: skipOptions,
		onError(apolloError: ApolloErrorInterop) {
			skipQueryIfUnauthorized = apolloError?.graphQLErrors?.some(
				(graphQLError: GraphQLError) =>
					isStatusCodeError(graphQLError, '401') || isUnauthorizedError(graphQLError),
			);
		},
	};

	const { data, loading, error } = expValEquals(
		'confluence_deprecate_experimental_personalization',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalSitePersonalizationQuery(queryArgs)
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<SitePersonalizationQueryData, SitePersonalizationQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
				SitePersonalizationQuery,
				queryArgs,
			);
	const attributes: SitePersonalizationAttributes[] =
		data?.experimentalSitePersonalization?.attributes || [];
	return {
		loading,
		error,
		attributes,
	};
};
